import { useEffect, useState, type FormEvent } from "react";
import type { Category } from "@shared/types";
import { api, ApiError } from "@/lib/api";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/components/Button";
import { TrashIcon } from "@/components/icons";

export function AdminCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});

  const load = () => api.admin.categories.list().then(setCategories).finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setError(null);
    try {
      await api.admin.categories.create({ name: newName.trim() });
      setNewName("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create category");
    }
  };

  const handleRename = async (id: string) => {
    const name = editing[id]?.trim();
    if (!name) return;
    try {
      await api.admin.categories.update(id, { name });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to rename category");
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? Products in it become uncategorized — they aren't deleted.`)) return;
    await api.admin.categories.remove(id);
    load();
  };

  return (
    <AdminLayout>
      <h1 className="mb-2 font-display text-xl font-medium text-ink">Categories</h1>
      <p className="mb-6 text-sm text-stone-500">
        These populate the category chips on the storefront home page and the category picker on each product.
      </p>

      <form onSubmit={handleCreate} className="mb-8 flex max-w-md gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category name (e.g. Wallets)"
          className="flex-1 rounded-lg border border-stone-300 bg-white px-3.5 py-2.5 text-sm text-ink focus:border-ink focus:outline-none"
        />
        <Button type="submit">Add</Button>
      </form>

      {error && <p className="mb-4 text-sm text-error">{error}</p>}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : (
        <div className="max-w-md overflow-hidden rounded-xl border border-stone-300 bg-white">
          {categories.map((cat, i) => (
            <div
              key={cat.id}
              className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? "border-t border-stone-100" : ""}`}
            >
              <input
                defaultValue={cat.name}
                onChange={(e) => setEditing((s) => ({ ...s, [cat.id]: e.target.value }))}
                onBlur={() => handleRename(cat.id)}
                onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                className="flex-1 rounded border border-transparent bg-transparent px-1.5 py-1 text-sm text-ink hover:border-stone-300 focus:border-ink focus:outline-none"
              />
              <span className="text-xs text-stone-500">/{cat.slug}</span>
              <button
                type="button"
                onClick={() => handleDelete(cat.id, cat.name)}
                aria-label={`Delete ${cat.name}`}
                className="text-stone-500 hover:text-error"
              >
                <TrashIcon width={16} height={16} />
              </button>
            </div>
          ))}
          {categories.length === 0 && <p className="px-4 py-6 text-center text-sm text-stone-500">No categories yet.</p>}
        </div>
      )}
    </AdminLayout>
  );
}
